import React, { useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import format from "date-fns/format";
import { isEmpty } from "lodash";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import waterIntakeCardLogo from "@/assets/images/water-intake-log-icon.png";
import { parseTime } from "@/components/formatters/TimeFormat";
import { EditIconButton } from "@/components/IconButtons";
import NoWaterIntakeIcon from "@/components/Icons/NoWaterIntakeIcon";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import {
  ClientWaterIntakeFragment,
  DeleteWaterIntakeInput,
} from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import AddGoalButton from "@/pages/Clients/Buttons/AddGoalButton";
import AddPastLogsButton from "@/pages/Clients/Buttons/AddPastLogsButton";
import { colors } from "@/theme/overrides/colors";

import AddWaterGoalModal from "./components/AddWaterGoalModal";
import AddWaterIntakeModal from "./components/AddWaterIntakeModal";
import EditWaterGoalModal from "./components/EditWaterGoalModal";
import OptionMenu from "./components/OptionMenu";
import WaterIntakeBarGraph from "./components/WaterIntakeBarGraph";
import WaveView from "./components/WaveView";

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(0, 4),
  },
  wrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingInline: "4px",
  },
  waterIntakeWrapper: {
    backgroundColor: colors.white,
    display: "flex",
    flexDirection: "row",
    minHeight: "95%",
    padding: "12px",
    marginTop: "18px",
  },
  waterIntakebowlContainer: {
    display: "flex",
    width: "50%",
    position: "relative",
    flexDirection: "column",
  },
  waterIntakeLabel: {
    display: "flex",
    flexDirection: "column",
  },
  waterIntakeBowl: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    flexDirection: "column",
    margin: "auto",
  },
  waterIntakeGoalContainer: {
    display: "flex",
    background: colors.light,
    padding: "10px 5px 10px 5px",
    marginRight: "10px",
    justifyContent: "space-between",
  },
  logsContainer: {
    height: "240px",
    overflowY: "auto",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
    paddingTop: "18px",
  },
  waterIntakelogsColumn: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "100%",
    marginLeft: "24px",
    minWidth: "50%",
  },

  waterIntakeChart: {
    display: "flex",
    width: "476px",
    backgroundColor: colors.white,
    marginLeft: "12px",
    marginTop: "18px",
    minHeight: "95%",
    justifyContent: "center",
    alignItems: "center",
  },
  waterIntakelogsContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
  waterIntakelogCardWrapper: {
    display: "flex",
    flexDirection: "row",
    minWidth: "100%",
    marginTop: "10px",
    marginBottom: "10px",
  },
  waterIntakelogCardContent: {
    display: "flex",
    flexDirection: "column",
    minWidth: "100%",
    padding: "4px 0px",
  },

  waterIntakelogCard: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingRight: "24px",
    marginLeft: "14px",
    minWidth: "90%",
  },
  bowl: {
    objectFit: "cover",
    width: "178px",
    height: "178px",
  },
  droplet: {
    objectFit: "cover",
    height: "28px",
    width: "28px",
  },
  noLog: {
    objectFit: "cover",
    height: "56px",
    width: "88px",
  },
  waterIntakeTotal: {
    fontSize: "14px",
    position: "absolute",
    alignSelf: "center",
    top: "51%",
    zIndex: 99,
  },
  waterIntakeCount: {
    position: "absolute",
    fontSize: "16px",
    fontWeight: "bold",
    top: "40%",
    left: "62%%",
    zIndex: 99,
  },
  log: {
    fontSize: "16px",
    color: colors.dark,
  },
  logTime: {
    fontSize: "14px",
    color: colors.grey,
  },
  logsHeader: {
    fontSize: "20px",
    fontWeight: 700,
    marginBottom: "8px",
    display: "flex",
    justifyContent: "space-between",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  goalButton1: {
    height: "30px !important",
  },
}));

const StyledNoWaterIntakeIcon = withStyles(NoWaterIntakeIcon, {
  root: {
    objectFit: "cover",
    height: "56px",
    width: "88px",
  },
});

export const waterIntakePageQuery = graphql(`
  query WaterIntakePage($date: ISO8601Date, $clientId: ID!) {
    dailyWaterIntake(date: $date, clientId: $clientId) {
      totalAmountMl
      logs {
        ...ClientWaterIntake
      }
    }

    client(id: $clientId) {
      id
      journalGoal {
        id
        targetWaterIntakeMl
      }
    }
  }

  fragment ClientWaterIntake on WaterIntake {
    id
    clientId
    amountMl
    drankAt
    drankOn
  }
`);

const deleteClientWaterIntakeMutation = graphql(`
  mutation DeleteClientWaterIntake($input: DeleteWaterIntakeInput!) {
    deleteWaterIntake(input: $input) {
      waterIntake {
        id
      }
    }
  }
`);

const WaterIntakePage: React.FC = () => {
  const { classes: styles } = useStyles();
  const { clientId, date } = useParams<{ clientId: string; date: string }>();
  const { data, loading } = useQuery(waterIntakePageQuery, {
    variables: { date: date, clientId: clientId ?? "" },
  });
  const waterIntake = data?.dailyWaterIntake?.totalAmountMl;
  const waterIntakeLogs = data?.dailyWaterIntake?.logs;
  const waterGoal = data?.client?.journalGoal?.targetWaterIntakeMl;
  const [currentWaterIntake, setCurrentWaterIntake] = useState<
    ClientWaterIntakeFragment | undefined
  >(undefined);
  const [deleteClientWaterIntake] = useMutation(
    deleteClientWaterIntakeMutation
  );
  const snackbar = useGlobalSnackbar();

  const addPastLog = useDialogState();
  const addWaterGoal = useDialogState();
  const editWaterGoal = useDialogState();

  const _handleEdit = (waterIntake: ClientWaterIntakeFragment) => {
    setCurrentWaterIntake(waterIntake);
    addPastLog.show();
  };

  const _handleAdd = () => {
    setCurrentWaterIntake(undefined);
    addPastLog.show();
  };

  const _handleDelete = async (id: string) => {
    try {
      const input: DeleteWaterIntakeInput = { id: id };
      const result = await deleteClientWaterIntake({
        variables: { input },
        refetchQueries: [
          { query: waterIntakePageQuery, variables: { clientId, date } },
        ],
      });

      if (result.data) {
        snackbar.open("Water intake deleted successfully!");
      }
    } catch {
      snackbar.open("Unable to delete water intake. Please try again later");
    }
  };

  const _handleCloseAddWaterIntakeModal = () => {
    setCurrentWaterIntake(undefined);
    addPastLog.hide();
  };

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <Typography variant="h6" color="textPrimary">
          Loading...
        </Typography>
      </Box>
    );
  }

  function getWaterIntakeLogs() {
    if (!isEmpty(waterIntakeLogs)) {
      return (
        <>
          <div className={styles.logsContainer}>
            {waterIntakeLogs?.map((log, index) => {
              const logFmt = log.drankAt != undefined ? log.drankAt : "";
              return (
                <Box key={index} className={styles.waterIntakelogCardContent}>
                  <Box className={styles.waterIntakelogCardWrapper}>
                    <img src={waterIntakeCardLogo} className={styles.droplet} />
                    <Box className={styles.waterIntakelogCard}>
                      <div>
                        <Typography className={styles.log}>
                          {log.amountMl}
                          {" ml"}
                        </Typography>
                        <Typography className={styles.logTime}>
                          {format(parseTime(logFmt), "hh:mm aa")}
                        </Typography>
                      </div>
                      <OptionMenu
                        onEdit={() => _handleEdit(log)}
                        onDelete={() => _handleDelete(log.id)}
                      />
                    </Box>
                  </Box>
                  <Divider />
                </Box>
              );
            })}
          </div>
        </>
      );
    } else {
      return (
        <div className={styles.emptyContainer}>
          <StyledNoWaterIntakeIcon />
          <Typography variant="subtitle2" color="textSecondary">
            No logs for this day
          </Typography>
        </div>
      );
    }
  }

  return (
    <div className={styles.root}>
      <Grid container>
        <Grid item xs={6}>
          <Box className={styles.waterIntakeWrapper}>
            <Box className={styles.waterIntakebowlContainer}>
              <Box className={styles.waterIntakeLabel}>
                <Typography
                  variant="h6"
                  color="textPrimary"
                  className={styles.logsHeader}
                >
                  Water Intake
                </Typography>
                <Box className={styles.waterIntakeGoalContainer}>
                  <Box display="flex">
                    <img src={waterIntakeCardLogo} className={styles.droplet} />
                    <Typography
                      variant="h3"
                      className={styles.log}
                      style={{
                        margin: "auto 0px auto 10px",
                      }}
                    >
                      Water Intake Goal
                    </Typography>
                  </Box>
                  {Features.waterIntakeGoalEnabled ? (
                    <EditIconButton onClick={editWaterGoal.show} />
                  ) : (
                    <AddGoalButton onClick={addWaterGoal.show} />
                  )}
                </Box>
              </Box>
              <Box className={styles.waterIntakeBowl}>
                <Typography
                  variant="subtitle2"
                  className={styles.waterIntakeCount}
                >
                  {waterIntake ?? 0}
                  {"ml"}
                  {" /"}
                </Typography>
                {Features.waterIntakeGoalEnabled && (
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    className={styles.waterIntakeTotal}
                  >
                    {waterGoal ?? 0}
                    {"ml"}
                  </Typography>
                )}
                <WaveView
                  waterIntakeTarget={waterGoal ?? 0}
                  waterIntakeTotal={waterIntake ?? 0}
                />
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box className={styles.waterIntakelogsColumn}>
              <Typography
                variant="h6"
                color="textPrimary"
                className={styles.logsHeader}
              >
                {format(new Date(date || new Date().toDateString()), "dd MMM")}
                &apos;s logs
                <AddPastLogsButton onClick={_handleAdd} />
              </Typography>
              {getWaterIntakeLogs()}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={5}>
          <WaterIntakeBarGraph
            clientId={clientId ?? ""}
            date={date ?? new Date().toDateString()}
            goal={waterGoal}
          />
        </Grid>
      </Grid>

      <AddWaterIntakeModal
        clientId={clientId ?? ""}
        date={date ?? new Date().toDateString()}
        onClose={_handleCloseAddWaterIntakeModal}
        visibility={addPastLog.shown}
        currentWaterIntake={currentWaterIntake}
      />
      <AddWaterGoalModal
        onClose={addWaterGoal.hide}
        visibility={addWaterGoal.shown}
      />
      <EditWaterGoalModal
        onClose={editWaterGoal.hide}
        visibility={editWaterGoal.shown}
        currentWaterGoal={waterGoal ? waterGoal.toString() : "0"}
      />
    </div>
  );
};

export default WaterIntakePage;
